import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Connects to data-controller="mail-template"
export default class extends Controller {
  static targets = [
    "checkbox",
    "input",
    "example",
    "disable",
    "invitedAt",
    "deadline",
    "reminderInput",
    "reminderExample",
    "reminderAt",
    "inviteFields",
    "reminderFields",
  ]
  static values = {
    candidate: String,
    titles: String,
    deadline: String,
  }

  inputTargetConnected() {
    this.toggleMailTemplateIdField()
  }

  reminderInputTargetConnected() {
    this.toggleReminderMailTemplateIdField()
  }

  updateDeadlineMinDate() {
    if (this.invitedAtTarget.value && this.reminderAtTarget.value) {
      if (this.reminderAtTarget.min != this.invitedAtTarget.value) {
        this.reminderAtTarget.min = this.invitedAtTarget.value
      }
      this.deadlineTarget.min = this.reminderAtTarget.value
    } else if (this.invitedAtTarget.value) {
      this.deadlineTarget.min = this.invitedAtTarget.value
      this.reminderAtTarget.min = this.invitedAtTarget.value
    } else if (this.reminderAtTarget.value) {
      this.deadlineTarget.min = this.reminderAtTarget.value
      this.reminderAtTarget.min = new Date().toISOString().split("T")[0]
    } else {
      this.deadlineTarget.min = new Date().toISOString().split("T")[0]
      this.reminderAtTarget.min = new Date().toISOString().split("T")[0]
    }
  }

  show_template() {
    let mail_template_id = this.inputTarget.value
    let mailable =
      (!this.hasInvitedAtTarget ||
        this.invitedAtTarget.value !== "" ||
        (this.hasCheckboxTarget && this.checkboxTarget.checked)) &&
      mail_template_id !== ""

    if (mailable) {
      const encodedTitles = encodeURIComponent(this.titlesValue)
      const encodedDeadline = encodeURIComponent(this.deadlineValue)
      get(
        `/assessments/mail_templates/${mail_template_id}?candidate_id=${this.candidateValue}&titles=${encodedTitles}&deadline=${encodedDeadline}`,
        {
          responseKind: "turbo-stream",
        },
      )
    } else {
      this.exampleTarget.innerHTML = ""
    }
  }

  show_reminder_template() {
    let reminder_mail_template_id = this.reminderInputTarget.value
    if (
      this.reminderAtTarget.value !== "" &&
      reminder_mail_template_id !== ""
    ) {
      const encodedTitles = encodeURIComponent(this.titlesValue)
      const encodedDeadline = encodeURIComponent(this.deadlineValue)
      get(
        `/assessments/mail_templates/${reminder_mail_template_id}?candidate_id=${this.candidateValue}&titles=${encodedTitles}&deadline=${encodedDeadline}&reminder=true`,
        {
          responseKind: "turbo-stream",
        },
      )
    } else {
      this.reminderExampleTarget.innerHTML = ""
    }
  }

  toggle_invite_template() {
    if (this.checkboxTarget.checked) {
      this.clearInvitedAtDateAndDisable()
    } else {
      this.enableInvitedAt()
    }

    this.toggleInviteFieldsVisibility()
    this.show_template()
  }

  clearInvitedAtDateAndDisable() {
    if (this.hasInvitedAtTarget) {
      const date = new Date().toISOString().split("T")[0]
      this.invitedAtTarget.value = date
      this.invitedAtTarget.disabled = true
      this.updateInvitedAtHiddenFields()
    }
  }

  enableInvitedAt() {
    if (this.hasInvitedAtTarget) {
      this.invitedAtTarget.value = ""
      this.invitedAtTarget.disabled = false
    }
  }

  updateInvitedAtHiddenFields() {
    document
      .querySelectorAll(".assignment-invited-at")
      .forEach((hiddenField) => {
        hiddenField.value = this.invitedAtTarget.value
      })
  }

  updateDeadlineHiddenFields() {
    const value = this.deadlineTarget.value
    document.querySelectorAll(".assignment-end-date").forEach((hiddenField) => {
      hiddenField.value = value
    })
    this.deadlineValue = event.target.value
  }

  toggleMailTemplateIdField() {
    requestAnimationFrame(() => {
      const invitedAtHasValue =
        this.hasInvitedAtTarget && this.invitedAtTarget.value !== ""
      const checkboxIsChecked =
        this.hasCheckboxTarget && this.checkboxTarget.checked
      const onlyHasInputTarget =
        !this.hasInvitedAtTarget && !this.hasCheckboxTarget
      const enable =
        invitedAtHasValue || checkboxIsChecked || onlyHasInputTarget

      if (this.hasInputTarget && this.inputTarget.tomselect) {
        if (enable) {
          this.inputTarget.tomselect.enable()
        } else {
          this.inputTarget.tomselect.disable()
        }
      }

      this.toggleInviteFieldsVisibility()

      const event = new CustomEvent("formStateChanged", { bubbles: true })
      this.element.dispatchEvent(event)
    })
  }

  toggleReminderMailTemplateIdField() {
    requestAnimationFrame(() => {
      const reminderAtHasValue =
        this.hasReminderAtTarget && this.reminderAtTarget.value !== ""
      const enable = reminderAtHasValue

      if (this.hasReminderInputTarget && this.reminderInputTarget.tomselect) {
        if (enable) {
          this.reminderInputTarget.tomselect.enable()
        } else {
          this.reminderInputTarget.tomselect.disable()
        }
      }

      this.toggleReminderFieldsVisibility()

      const event = new CustomEvent("formStateChanged", { bubbles: true })
      this.element.dispatchEvent(event)
    })
  }

  toggleInviteFieldsVisibility() {
    if (!this.hasInviteFieldsTarget) return

    const invitedAtHasValue =
      this.hasInvitedAtTarget && this.invitedAtTarget.value !== ""
    const checkboxIsChecked =
      this.hasCheckboxTarget && this.checkboxTarget.checked
    const show = invitedAtHasValue || checkboxIsChecked

    this.inviteFieldsTarget.classList.toggle("hidden", !show)
    this.dispatchVisibilityChanged()
  }

  toggleReminderFieldsVisibility() {
    if (!this.hasReminderFieldsTarget) return

    const reminderAtHasValue =
      this.hasReminderAtTarget && this.reminderAtTarget.value !== ""

    this.reminderFieldsTarget.classList.toggle("hidden", !reminderAtHasValue)
    this.dispatchVisibilityChanged()
  }

  dispatchVisibilityChanged() {
    const inviteVisible =
      this.hasInviteFieldsTarget &&
      !this.inviteFieldsTarget.classList.contains("hidden")
    const reminderVisible =
      this.hasReminderFieldsTarget &&
      !this.reminderFieldsTarget.classList.contains("hidden")

    const event = new CustomEvent("mail-template:visibilityChanged", {
      bubbles: true,
      detail: { inviteVisible, reminderVisible },
    })
    this.element.dispatchEvent(event)
  }
}
